import { Injectable, inject } from "@angular/core";


import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { Product, ProductInfo, ProductInfoForKey, ProductOptions, ProductKey, ToothArea } from "../models";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { MagentoProduct } from "libs/magento/src/lib/models";
import { DataTransferMethod, TreatmentModel } from "libs/treatments/src/lib/models/treatment.model";

@Injectable({
	providedIn: "root"
})
export class ProductInfoService {
	private translate = inject(TranslateService);
	private http = inject(HttpClient);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);

	baseUrl: string;

	constructor() {
		this.baseUrl = this.config.apiUrl;
	}

	public getProductNameHtml(
		productKey: ProductKey,
		variantSeparator: string = "<br>"
	): string {
		const productInfo: ProductInfo = ProductInfoForKey[productKey as ProductKey];
		let nameHtml = productInfo?.name ? this.translate.instant(productInfo.name) : "";
		if (productInfo?.variantName) {
			nameHtml += variantSeparator;
			nameHtml += this.translate.instant(productInfo.variantName);
		}
		return nameHtml;
	}

	public getProductOptions(treatment: TreatmentModel): ProductOptions {
		if (treatment.product?.options) {
			return treatment.product.options as ProductOptions;
		} else {
			// Case: LEGACY Magento treatment without product options. We need to "interpret" the _actual_ order articles.
			const products = treatment.orders.at(-1)?.products ?? [];
			return this.getProductOptionsFromArticles(products);
		}
	}

	public getProductOptionsFromArticles(articles: MagentoProduct[]): ProductOptions {
		const productOptions: ProductOptions = {
			dataTransfer: DataTransferMethod.UPLOAD,
			lj: ToothArea.NONE,
			uj: ToothArea.NONE,
		};
		articles.forEach((element) => {
			if (this.isLowerJawArticle(element.sku)) {
				productOptions.lj = element.options["Zahnbereich"];
			}
			if (this.isUpperJawArticle(element.sku)) {
				productOptions.uj = element.options["Zahnbereich"];
			}
		});
		return productOptions;
	}

	public filterProductKeysAllowedForUser(productKeys?: ProductKey[], customerId?: string): Observable<ProductKey[]> {
		const params: Record<string, string | string[]> = {};
		if (productKeys?.length) {
			params["productKeys[]"] = productKeys;
		}
		if (customerId) {
			params["customerId"] = customerId;
		}
		return this.http.get<ProductKey[]>(`${this.baseUrl}/products/keysAllowedForUser`, { params });
	}

	public isUserAllowedForProduct(productKey: ProductKey): Observable<boolean> {
		return this.http.get<boolean>(`${this.baseUrl}/products/${productKey}/isAllowed`);
	}

	private isUpperJawArticle(sku: string) {
		// XXX This logic will need to change with SAP.
		return sku.endsWith("5010.1");
	}

	private isLowerJawArticle(sku: string) {
		// XXX This logic will need to change with SAP.
		return sku.endsWith("5020.1");
	}

	public getToothAreaForProduct(product: Product, isUpperJaw: boolean) : string {
		if(!product){
			return '';
		}
		const options: Partial<ProductOptions> = product.options;
		//handle memotain
		if (product.key === ProductKey.MEMOTAIN) {
			//handle legacy treatments pre 2.0.0
			if (options?.custom?.zahnbereich) {
				return isUpperJaw
					? options.custom.zahnbereich.uj?.value
					: options.custom.zahnbereich.lj?.value;
			}
		}

		//handle legacy treatments pre 2.0.0
		if (product.info?.toothArea) {
			return product.info?.toothArea ?? "";
		}
		if (!product?.options){
			return '';
		}
		return isUpperJaw ? options?.uj : options?.lj;
	}
}
